import type { Poll, PollState } from '@pin/schemas/poll';
import {
  catchError,
  firstValueFrom,
  NEVER,
  repeat,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  timeout,
  timer,
} from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { withIdToken } from '../firebase/auth';
import { isVisible$ } from '../utils/rxjs.dom';
import {
  checkNoContentSuccess,
  HttpError,
  retryWithBackoff,
} from '../utils/rxjs.http';

const fetchPolls$ = withIdToken((token) =>
  fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/polls`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    selector: (r): Promise<PollState> => {
      if (!r.ok) {
        throw new HttpError(r);
      }
      return r.json();
    },
  }).pipe(timeout(6_000), retryWithBackoff(3))
);

export const pollSubject$ = new Subject<void>();
export const pollPolls = (errorDelay = 5_000, repeatDelay = 2_500) =>
  isVisible$.pipe(
    switchMap((visible) => {
      if (!visible) {
        return NEVER;
      }
      return pollSubject$.pipe(
        startWith(0),
        switchMap(() =>
          fetchPolls$.pipe(
            catchError((_err: unknown, source) =>
              timer(errorDelay).pipe(switchMap(() => source))
            ),
            repeat({ delay: () => timer(repeatDelay) })
          )
        )
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

export const createPoll = async (payload: {
  question: string;
  answers: string[];
}) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/polls`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const editPoll = async (
  id: string,
  payload: { question: string; answers: string[] }
) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/polls/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'PATCH',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const deletePoll = async (pollId: string) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/polls/${pollId}`, {
        headers: { Authorization: `Bearer ${token}` },
        method: 'DELETE',
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const setActivePoll = async (payload: Poll) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/polls/live`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const setPollClosed = async () => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/polls/inactive`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

/** Put the poll's result on air in the overlay, replacing any other. */
export const showPollResult = async (pollId: string) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/polls/result`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'PUT',
        body: JSON.stringify({ id: pollId }),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const hidePollResult = async () => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/polls/result`, {
        headers: { Authorization: `Bearer ${token}` },
        method: 'DELETE',
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};
